import {
  useCallback,
  useEffect,
  useRef,
  useState,
  type KeyboardEvent,
  type MouseEvent,
  type PointerEvent,
  type ReactNode,
} from "react";
import { useTranslation } from "@thyra/landing-i18n";
import {
  carouselKeyTarget,
  isDragGesture,
  nearestIndex,
  perViewOf,
  stepIndex,
  visibleRange,
} from "../../lib/carousel-math";
import { ScrollArrow } from "./ScrollArrow";
import { prefersReducedMotion } from "../../lib/motion";
import { useScrollEdges } from "./useScrollEdges";

/**
 * Card width: two per row from md up, one below. `min-w-0` is what holds it: a flex item defaults to
 * `min-width: auto`, so one unbreakable line (the "works with" names, the hood row) would stretch that card
 * past its basis and make the cards uneven and the snap/position maths wrong.
 */
export const CAROUSEL_ITEM_CLASS =
  "min-w-0 shrink-0 basis-full snap-start md:basis-[calc((100%-1.25rem)/2)]";

type AgentCarouselProps = {
  /** Number of cards in `children` (each child is one <li>). */
  count: number;
  /** Changes when the list is replaced (a new filter); the carousel jumps back to the first card. */
  resetKey: unknown;
  children: ReactNode;
};

/** Wait for smooth scrolling to settle before the position is read out to assistive tech. */
const ANNOUNCE_DELAY_MS = 500;

type Press = { x: number; y: number; scroll: number; dragging: boolean };

/**
 * Horizontal scroll-snap carousel: two cards on desktop, one on phones. Native scrolling does the heavy
 * lifting (touch, trackpad, horizontal wheel, scrollbar-less snap); this adds previous/next buttons,
 * Arrow/Home/End keys, mouse drag-to-scroll and a "3-4 of 40" position that does not grow with the list.
 */
export function AgentCarousel({
  count,
  resetKey,
  children,
}: AgentCarouselProps) {
  const { t } = useTranslation("sections");
  const { ref, edges, measure } = useScrollEdges<HTMLDivElement>(count);
  const [range, setRange] = useState<{ first: number; last: number } | null>(
    null,
  );
  const [interacted, setInteracted] = useState(false);
  const [announced, setAnnounced] = useState("");
  const press = useRef<Press | null>(null);
  const suppressClick = useRef(false);

  const geometry = useCallback(() => {
    const el = ref.current;
    const items = el?.firstElementChild
      ? (Array.from(el.firstElementChild.children) as HTMLElement[])
      : [];
    if (!el || items.length === 0) return null;
    const itemWidth = items[0].offsetWidth;
    const gap =
      items.length > 1
        ? items[1].offsetLeft - items[0].offsetLeft - itemWidth
        : 0;
    return {
      el,
      items,
      itemWidth,
      gap,
      stride: itemWidth + gap,
      perView: perViewOf(el.clientWidth, itemWidth, gap, items.length),
      range: visibleRange({
        scrollLeft: el.scrollLeft,
        scrollWidth: el.scrollWidth,
        clientWidth: el.clientWidth,
        itemWidth,
        gap,
        count: items.length,
      }),
    };
  }, [ref]);

  // Keep the position indicator in step with scrolling (rAF-throttled) and with layout changes.
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    let frame = 0;
    const update = () => {
      frame = 0;
      const next = geometry()?.range ?? null;
      setRange((prev) =>
        prev?.first === next?.first && prev?.last === next?.last ? prev : next,
      );
    };
    const schedule = () => {
      if (!frame) frame = requestAnimationFrame(update);
    };
    update();
    el.addEventListener("scroll", schedule, { passive: true });
    const observer =
      typeof ResizeObserver !== "undefined"
        ? new ResizeObserver(schedule)
        : undefined;
    observer?.observe(el);
    return () => {
      if (frame) cancelAnimationFrame(frame);
      el.removeEventListener("scroll", schedule);
      observer?.disconnect();
    };
  }, [ref, geometry, count]);

  // A new filter means a new list: start at the first card, instantly (no sweep across the old list).
  useEffect(() => {
    ref.current?.scrollTo({ left: 0, behavior: "auto" });
    measure();
  }, [resetKey, ref, measure]);

  const scrollToIndex = useCallback(
    (index: number) => {
      const g = geometry();
      const target = g?.items[index];
      if (!g || !target) return;
      g.el.scrollTo({
        left: target.offsetLeft - g.items[0].offsetLeft,
        behavior: prefersReducedMotion() ? "auto" : "smooth",
      });
    },
    [geometry],
  );

  function step(direction: -1 | 1) {
    const g = geometry();
    if (!g?.range) return;
    setInteracted(true);
    scrollToIndex(stepIndex(g.range.first, direction, count, g.perView));
  }

  function onKeyDown(event: KeyboardEvent<HTMLDivElement>) {
    if (event.altKey || event.ctrlKey || event.metaKey || event.shiftKey)
      return;
    const g = geometry();
    if (!g?.range) return;
    const target = carouselKeyTarget(
      g.range.first,
      event.key,
      count,
      g.perView,
    );
    if (target === null) return;
    event.preventDefault();
    setInteracted(true);
    scrollToIndex(target);
  }

  // Mouse drag-to-scroll. Touch and pen already scroll natively, so only the mouse is handled. Capture
  // starts only after the pointer travels past a threshold, so a plain click still reaches the card link.
  function onPointerDown(event: PointerEvent<HTMLDivElement>) {
    if (event.pointerType !== "mouse" || event.button !== 0) return;
    const el = event.currentTarget;
    if (!edges.overflowing) return;
    press.current = {
      x: event.clientX,
      y: event.clientY,
      scroll: el.scrollLeft,
      dragging: false,
    };
    el.style.userSelect = "none";
  }

  function onPointerMove(event: PointerEvent<HTMLDivElement>) {
    const p = press.current;
    if (!p) return;
    const el = event.currentTarget;
    // The button was released outside the window without a pointerup reaching us: stop following the mouse.
    if (event.buttons === 0) {
      endPress(event);
      return;
    }
    const dx = event.clientX - p.x;
    if (!p.dragging) {
      if (!isDragGesture(dx, event.clientY - p.y)) return;
      p.dragging = true;
      el.setPointerCapture(event.pointerId);
      el.style.scrollSnapType = "none"; // snapping would fight every pixel of the drag
      el.dataset.dragging = "true";
    }
    el.scrollLeft = p.scroll - dx;
  }

  function endPress(event: PointerEvent<HTMLDivElement>) {
    const p = press.current;
    press.current = null;
    if (!p) return;
    const el = event.currentTarget;
    el.style.userSelect = "";
    if (!p.dragging) return;
    delete el.dataset.dragging;
    if (el.hasPointerCapture(event.pointerId)) {
      el.releasePointerCapture(event.pointerId);
    }
    // The click that follows a drag must not follow the link under the cursor.
    suppressClick.current = true;
    window.setTimeout(() => {
      suppressClick.current = false;
    }, 0);
    const g = geometry();
    el.style.scrollSnapType = "";
    if (g) {
      setInteracted(true);
      scrollToIndex(nearestIndex(el.scrollLeft, g.stride, count, g.perView));
    }
  }

  // Never leave the scroller unselectable or snap-less: unmounting mid-drag skips every pointer event.
  useEffect(() => {
    const el = ref.current;
    return () => {
      if (!el) return;
      el.style.userSelect = "";
      el.style.scrollSnapType = "";
      delete el.dataset.dragging;
    };
  }, [ref]);

  function onClickCapture(event: MouseEvent<HTMLDivElement>) {
    if (!suppressClick.current) return;
    event.preventDefault();
    event.stopPropagation();
  }

  const positionText = range
    ? range.first === range.last
      ? t("assistantRoles.positionSingle", {
          index: range.first + 1,
          total: count,
        })
      : t("assistantRoles.position", {
          from: range.first + 1,
          to: range.last + 1,
          total: count,
        })
    : "";

  // Announce the position after scrolling settles, and only once the visitor has used the controls.
  useEffect(() => {
    const id = window.setTimeout(
      () => setAnnounced(interacted ? positionText : ""),
      ANNOUNCE_DELAY_MS,
    );
    return () => window.clearTimeout(id);
  }, [interacted, positionText]);

  const thumbLeft = range ? (range.first / count) * 100 : 0;
  const thumbWidth = range
    ? ((range.last - range.first + 1) / count) * 100
    : 100;

  return (
    <div className="mt-8">
      <div
        ref={ref}
        role="region"
        aria-label={t("assistantRoles.carouselLabel")}
        tabIndex={0}
        data-fade-start={edges.overflowing && !edges.atStart}
        data-fade-end={edges.overflowing && !edges.atEnd}
        onKeyDown={onKeyDown}
        onPointerDown={onPointerDown}
        onPointerMove={onPointerMove}
        onPointerUp={endPress}
        onPointerCancel={endPress}
        onLostPointerCapture={endPress}
        onClickCapture={onClickCapture}
        onDragStart={(event) => event.preventDefault()}
        className={`agent-scroll edge-fade relative -mx-2 -my-3 snap-x snap-mandatory overflow-x-auto overscroll-x-contain rounded-2xl px-2 py-3 scroll-pl-2 focus-visible:outline-2 focus-visible:-outline-offset-2 focus-visible:outline-primary data-[dragging=true]:cursor-grabbing ${
          edges.overflowing ? "cursor-grab" : ""
        }`}
      >
        <ul className="flex gap-5">{children}</ul>
      </div>

      <div
        className={`mt-6 flex items-center gap-4 ${
          edges.overflowing ? "" : "invisible"
        }`}
        aria-hidden={!edges.overflowing}
      >
        <p className="min-w-[5.5rem] text-sm font-medium tabular-nums text-muted-foreground-strong">
          {positionText}
        </p>
        <div
          aria-hidden
          className="relative h-1 flex-1 overflow-hidden rounded-full bg-muted"
        >
          <span
            className="absolute inset-y-0 rounded-full bg-primary/70 transition-[left,width] duration-200 motion-reduce:transition-none"
            style={{ left: `${thumbLeft}%`, width: `${thumbWidth}%` }}
          />
        </div>
        <div className="flex items-center gap-2">
          <ScrollArrow
            direction="previous"
            label={t("assistantRoles.previous")}
            inactive={edges.atStart}
            onPress={() => step(-1)}
          />
          <ScrollArrow
            direction="next"
            label={t("assistantRoles.next")}
            inactive={edges.atEnd}
            onPress={() => step(1)}
          />
        </div>
      </div>

      <p role="status" aria-live="polite" aria-atomic className="sr-only">
        {announced}
      </p>
    </div>
  );
}
